import copyJson from 'main/data/med-school/copy.json';
import percentilesJson from 'main/data/med-school/mcatPercentiles.json';
import schoolsJson from 'main/data/med-school/schools.json';

import { ordinal } from './ordinal';
import {
  type Band,
  type Country,
  type MedSchoolCopy,
  type PercentileData,
  type ProgramType,
  type School,
  SCORE_MAX,
  SCORE_MIN,
  SECTION_KEYS,
  SECTION_MAX,
  SECTION_MIN,
  type SectionKey,
  type SectionMinimums,
} from './types';

export const COPY: MedSchoolCopy = copyJson;
export const PERCENTILES: PercentileData = percentilesJson;
export const BANDS: readonly Band[] = COPY.bands;

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function sourcePeriod(value: unknown): string {
  return asString(value) ?? (isRecord(value) ? asString(value.value) : null) ?? '';
}

function fieldSources(value: unknown): School['field_sources'] {
  if (!isRecord(value)) return {};
  const sources: Record<string, { period: string; url: string }> = {};
  for (const [field, source] of Object.entries(value)) {
    if (!isRecord(source) || typeof source.url !== 'string' || !source.url.startsWith('https://')) continue;
    sources[field] = { period: asString(source.period) ?? 'Cohort not specified', url: source.url };
  }
  return sources;
}

export function statisticLabel(stat: string | null, metric: 'GPA' | 'MCAT'): string {
  return `${
    stat === 'mean' ? 'Mean'
    : stat === 'median' ? 'Median'
    : 'Published'
  } ${metric}`;
}

function asCountry(value: unknown): Country | null {
  return value === 'US' || value === 'CA' ? value : null;
}

function asProgramType(value: unknown): ProgramType | null {
  return value === 'MD' || value === 'DO' ? value : null;
}

function asSectionMinimums(value: unknown): SectionMinimums | null {
  if (!isRecord(value)) return null;
  const minimums: SectionMinimums = {
    bbf: asNumber(value.bbf),
    cars: asNumber(value.cars),
    cpf: asNumber(value.cpf),
    psf: asNumber(value.psf),
  };
  return SECTION_KEYS.some(key => minimums[key] !== null) ? minimums : null;
}

function parseSchool(value: unknown): School | null {
  if (!isRecord(value)) return null;
  const key = asString(value.key);
  const name = asString(value.name);
  const country = asCountry(value.country);
  const type = asProgramType(value.type);
  if (key === null || name === null || country === null || type === null) return null;

  return {
    acceptance_rate: asNumber(value.acceptance_rate),
    as_of: sourcePeriod(value.as_of),
    cars_minimum: asNumber(value.cars_minimum),
    city: asString(value.city) ?? '',
    class_size: asNumber(value.class_size),
    country,
    field_sources: fieldSources(value.field_sources),
    gpa_stat: asString(value.gpa_stat),
    in_region_share: asNumber(value.in_region_share),
    key,
    mcat_minimum: asNumber(value.mcat_minimum),
    mcat_stat: asString(value.mcat_stat),
    mcat_usage: asString(value.mcat_usage) ?? 'unknown',
    median_gpa: asNumber(value.median_gpa),
    median_mcat: asNumber(value.median_mcat),
    name,
    region: asString(value.region) ?? '',
    region_name: asString(value.region_name) ?? '',
    requirements_source_url: asString(value.requirements_source_url),
    requirements_note: asString(value.requirements_note),
    residency_note: asString(value.residency_note),
    section_minimums: asSectionMinimums(value.section_minimums),
    source_url: asString(value.source_url),
    type,
    url: asString(value.url),
  };
}

export function parseSchools(value: unknown): readonly School[] {
  if (!isRecord(value) || !Array.isArray(value.schools)) return [];
  const parsed: School[] = [];
  for (const entry of value.schools) {
    const school = parseSchool(entry);
    if (school !== null) parsed.push(school);
  }
  return parsed;
}

function parseAsOf(value: unknown): string {
  return (isRecord(value) && asString(value.as_of)) || '';
}

export const SCHOOLS: readonly School[] = parseSchools(schoolsJson);
export const SCHOOLS_AS_OF: string = parseAsOf(schoolsJson);

export function isValidScore(value: number): boolean {
  return Number.isInteger(value) && value >= SCORE_MIN && value <= SCORE_MAX;
}

export function parseScore(value: string | undefined): number | null {
  if (value === undefined || !/^\d+$/.test(value)) return null;
  const score = Number(value);
  return isValidScore(score) ? score : null;
}

export function isValidSectionScore(value: number): boolean {
  return Number.isInteger(value) && value >= SECTION_MIN && value <= SECTION_MAX;
}

export const ALL_SCORES: readonly number[] = Array.from(
  { length: SCORE_MAX - SCORE_MIN + 1 },
  (_unused, index) => SCORE_MIN + index,
);

export const ALL_SECTION_SCORES: readonly number[] = Array.from(
  { length: SECTION_MAX - SECTION_MIN + 1 },
  (_unused, index) => SECTION_MIN + index,
);

export function totalPercentile(score: number): number | null {
  return PERCENTILES.total[String(score)] ?? null;
}

export function sectionPercentile(section: SectionKey, value: number): number | null {
  return PERCENTILES.sections[section][String(value)] ?? null;
}

export function formatPercentile(percentile: number | null): string {
  if (percentile === null) return 'unknown';
  return percentile === 0 ? '<1' : String(percentile);
}

export function formatPercentileOrdinal(percentile: number | null): string {
  if (percentile === null) return 'unknown';
  return percentile === 0 ? '<1st' : ordinal(percentile);
}

export function bandForScore(score: number): Band | null {
  return BANDS.find(band => score >= band.min && score <= band.max) ?? null;
}

export function schoolProgramGroup(school: School): 'ca' | 'do' | 'md' {
  if (school.country === 'CA') return 'ca';
  return school.type === 'DO' ? 'do' : 'md';
}
